import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { AppDispatch, RootState } from "../../../redux/store";
import { createAssessment } from "../../../redux/actions/assessmentActions";
import { fetchOrganizations } from "../../../redux/actions/organizationActions";

interface CreateAssessmentDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId?: string;
}

const CreateAssessmentDialog: React.FC<CreateAssessmentDialogProps> = ({
  open,
  onClose,
  organizationId,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { user: currentUser } = useSelector((state: RootState) => state.base);
  const { organizations } = useSelector(
    (state: RootState) => state.organizations
  );
  const { loading } = useSelector((state: RootState) => state.assessments);

  const [formData, setFormData] = useState({
    organization_id: organizationId || currentUser?.organization_id || "",
    scenario_description: "",
    environment: "production",
    display_title: "",
    category: "",
  });

  useEffect(() => {
    if (open) {
      // Only fetch organizations if we're not already in an organization context
      if (!organizationId) {
        dispatch(fetchOrganizations());
      }
    }
  }, [open, dispatch, organizationId]);

  useEffect(() => {
    if (organizationId) {
      setFormData((prev) => ({
        ...prev,
        organization_id: organizationId,
      }));
    } else if (currentUser?.organization_id) {
      setFormData((prev) => ({
        ...prev,
        organization_id: currentUser.organization_id || "",
      }));
    }
  }, [currentUser, organizationId]);

  const handleChange = (field: string) => (event: any) => {
    const value = event.target.value;

    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleSubmit = async () => {
    if (!formData.scenario_description.trim()) {
      toast.error("Scenario description is required");
      return;
    }

    if (!formData.organization_id) {
      toast.error("Organization is required");
      return;
    }

    // Trim all string fields before sending to backend
    const trimmedFormData = {
      organization_id: formData.organization_id.trim(),
      scenario_description: formData.scenario_description.trim(),
      environment: formData.environment.trim(),
      display_title: formData.display_title.trim(),
      category: formData.category.trim(),
    };

    try {
      const result = await dispatch(createAssessment(trimmedFormData)).unwrap();
      toast.success("Assessment created successfully!");
      onClose();
      navigate(`/dashboard/assessments/${result.organization_id}/${result.id}`);
    } catch (error) {
      toast.error("Failed to create assessment");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Assessment</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Organization</InputLabel>
            <Select
              value={formData.organization_id}
              onChange={handleChange("organization_id")}
              label="Organization"
              disabled={!!organizationId}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Display Title (Optional)"
            fullWidth
            value={formData.display_title}
            onChange={handleChange("display_title")}
            placeholder="e.g., Home Care Assessment"
            helperText="A searchable title for this assessment (This does not change anything about assessment generation)"
          />

          <TextField
            label="Category (Optional)"
            fullWidth
            value={formData.category}
            onChange={handleChange("category")}
            placeholder="e.g., Press, FundRaising, Technical Requests etc."
            helperText="Category to display on the hub for this assessment type"
          />

          <Box sx={{ mb: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 1,
                fontWeight: 600,
                color: "text.primary",
                fontSize: "0.875rem",
              }}
            >
              Writing a Good Scenario Description
            </Typography>
            <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
              <Typography sx={{ mb: 1 }}>
                A good description should have:
              </Typography>
              <Box
                component="ul"
                sx={{
                  mb: 1,
                  pl: 2.5,
                }}
              >
                <li>A brief description of the interviewee (interviewing for sales role at Persona)</li>
                <li>Interview topic, and interviewer goals (hiring a sales person)</li>
                <li>Evaluation criteria for the interview (1–5 years of experience in outbound sales roles…)</li>
                <li>What an ideal candidate would look like (has experience with AI agents and/or in the hiring space)</li>
              </Box>
              <Typography sx={{ m: 0 }}>
                Try to be as <strong>specific</strong> as possible in what you're looking for.
              </Typography>
            </Box>
          </Box>

          <TextField
            label="Scenario Description"
            fullWidth
            multiline
            rows={3}
            value={formData.scenario_description}
            onChange={handleChange("scenario_description")}
            placeholder="Persona AI is hiring an enterprise salesperson to help expand our outbound sales efforts. We're looking for someone with 1–5 years of experience in outbound or full-cycle roles (SDR, BDR, AE) who thrives on building relationships and driving new opportunities. This is a remote position focused on execution and results, with plenty of room to grow.
Must haves:
1–5 years of outbound or full-cycle enterprise sales experience
Resilience and problem-solving skills in the face of rejection
Emotional intelligence when reflecting on customer interactions
Familiarity with modern sales tools (CRM, LinkedIn, email automation)
Nice to haves:
Experience selling into enterprise accounts across industries
Experience selling AI agents or hiring products
Demonstrated curiosity, self-motivation, and eagerness to learn
This is an initial screen and should be 15 minutes long. We don't need to know their location or verify employment status, as that will be done later."
            required
          />

          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Environment</InputLabel>
              <Select
                value={formData.environment}
                onChange={handleChange("environment")}
                label="Environment"
              >
                <MenuItem value="staging">Staging</MenuItem>
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="qa">QA</MenuItem>
                <MenuItem value="demo">Demo</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          Create Assessment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateAssessmentDialog;
